import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Globe, ArrowRight } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { SPANISH_BADGE } from "../i18n/strings";
import { trackEvent } from "../utils/analytics";

const STORAGE_KEY = "ninas_es_badge_seen_v2";
const SHOW_DELAY_MS = 6000;

/**
 * Floating badge for Spanish-speaking visitors on their first visit —
 * announces the new bilingual menu. Shows ONCE EVER per browser.
 *
 * Behaviour:
 *  - Only shows if navigator language starts with "es"
 *  - If user is currently in EN mode → CTA switches to ES
 *  - If user is already in ES mode → CTA links to /menu
 *  - Dismissed / accepted → never shown again
 */
export default function SpanishWelcomeBadge() {
  const { lang, setLang } = useLang();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const nav = (navigator.language || "").toLowerCase();
    if (!nav.startsWith("es")) return;
    try { if (localStorage.getItem(STORAGE_KEY)) return; } catch (e) { /* ignore */ }
    const t = setTimeout(() => {
      setVisible(true);
      trackEvent("spanish_badge_shown", { current_lang: lang });
    }, SHOW_DELAY_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = () => { try { localStorage.setItem(STORAGE_KEY, "1"); } catch (e) { /* ignore */ } };

  const dismiss = () => {
    persist();
    setVisible(false);
    trackEvent("spanish_badge_dismiss");
  };

  const accept = () => {
    persist();
    setVisible(false);
    if (lang !== "es") {
      setLang("es");
      trackEvent("spanish_badge_accept", { action: "switch_to_es" });
    } else {
      trackEvent("spanish_badge_accept", { action: "go_to_menu" });
    }
    navigate("/menu");
  };

  if (!visible) return null;

  const isEsMode = lang === "es";
  const title = isEsMode ? SPANISH_BADGE.title.es : SPANISH_BADGE.title.en;
  const body  = isEsMode ? SPANISH_BADGE.bodyEsMode.es : SPANISH_BADGE.bodyEnMode.en;
  const cta   = isEsMode ? SPANISH_BADGE.ctaSeeMenu.es : SPANISH_BADGE.ctaSwitchToEs.en;

  return (
    <div
      role="dialog"
      aria-live="polite"
      data-testid="spanish-badge"
      className="fixed bottom-6 left-6 z-[80] max-w-xs animate-popup-in"
    >
      <div className="relative bg-cream rounded-2xl shadow-2xl border border-brand/20 p-5 pr-10">
        <button
          onClick={dismiss}
          aria-label={SPANISH_BADGE.dismiss.es}
          data-testid="spanish-badge-close"
          className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-granite/50 hover:text-granite hover:bg-cream-100 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-brand/10 text-brand ring-1 ring-brand/25">
            <Globe className="w-4 h-4" />
          </span>
          <div className="font-hand text-lg text-brand leading-none">{title}</div>
        </div>
        <p className="text-[13px] text-granite/75 leading-relaxed mb-4">
          {body}
        </p>
        <button
          onClick={accept}
          data-testid="spanish-badge-cta"
          className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-cream px-4 py-2.5 rounded-full font-semibold text-sm transition-colors"
        >
          {cta}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
